import React from 'react';
import {
  Button,
  Badge,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ChevronUp20Regular, ChevronDown20Regular } from '@fluentui/react-icons';
import { BaseDialog } from './BaseDialog';
import { formatBusinessDate, formatBusinessTime } from '../lib/businessTime';

export type EmailDeliveryStatus =
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'bounced'
  | 'complained'
  | 'failed';

export interface EmailHistoryEntry {
  id: string;
  sentAt: string; // ISO timestamp
  senderName: string;
  subject: string;
  status: EmailDeliveryStatus;
  recipients?: string[];
  bodyPreview?: string;
  onViewEmail?: () => void;
}

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  groupHeader: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.padding('8px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorBrandStroke2),
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorBrandForeground2,
  },
  groupHeaderToday: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginRight: '6px',
  },
  entry: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('14px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    ':last-child': {
      borderBottomWidth: 0,
    },
  },
  entrySummary: {
    flex: 1,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  entrySubject: {
    fontWeight: 600,
  },
  entryTime: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
  },
  expandButton: {
    minWidth: 'auto',
  },
  details: {
    ...shorthands.padding('14px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '12px',
  },
  detailsLines: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: 0,
  },
  detailsLabel: {
    fontWeight: 600,
    marginRight: '4px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    ...shorthands.padding('60px', '32px'),
    gap: '8px',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  emptySubtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    maxWidth: '380px',
    lineHeight: 1.5,
  },
});

const STATUS_COLOR: Record<EmailDeliveryStatus, 'success' | 'informative' | 'warning' | 'danger' | 'subtle'> = {
  sent: 'informative',
  delivered: 'success',
  opened: 'informative',
  bounced: 'danger',
  complained: 'warning',
  failed: 'danger',
};

const STATUS_LABEL: Record<EmailDeliveryStatus, string> = {
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  bounced: 'Bounced',
  complained: 'Complained',
  failed: 'Failed',
};

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function formatGroupHeader(iso: string): { isToday: boolean; date: string } {
  const d = new Date(iso);
  const today = new Date();
  const isToday = startOfDay(d) === startOfDay(today);
  const date = formatBusinessDate(iso);
  return { isToday, date };
}

function formatTime(iso: string): string {
  return formatBusinessTime(iso);
}

function groupByDay(entries: EmailHistoryEntry[]): { key: string; iso: string; entries: EmailHistoryEntry[] }[] {
  const sorted = [...entries].sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
  const groups = new Map<string, { iso: string; entries: EmailHistoryEntry[] }>();
  for (const e of sorted) {
    const key = String(startOfDay(new Date(e.sentAt)));
    if (!groups.has(key)) groups.set(key, { iso: e.sentAt, entries: [] });
    groups.get(key)!.entries.push(e);
  }
  return Array.from(groups.entries()).map(([key, v]) => ({ key, ...v }));
}

interface EmailHistoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entries?: EmailHistoryEntry[];
}

const EmailHistoryDialog: React.FC<EmailHistoryDialogProps> = ({ isOpen, onClose, entries }) => {
  const styles = useStyles();
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const groups = React.useMemo(() => groupByDay(entries || []), [entries]);
  const hasEntries = (entries?.length || 0) > 0;

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Email history"
      maxWidth="640px"
      maxHeight="50vh"
      showCloseButton
      contentPadding="0"
    >
      {!hasEntries ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyTitle}>No email history to display</span>
          <span className={styles.emptySubtitle}>
            If you have just sent an email, it may take a short while for it to show up here.
          </span>
        </div>
      ) : (
        <div className={styles.list}>
          {groups.map((g) => {
            const { isToday, date } = formatGroupHeader(g.iso);
            return (
              <React.Fragment key={g.key}>
                <div className={styles.groupHeader}>
                  {isToday && <span className={styles.groupHeaderToday}>Today</span>}
                  {date}
                </div>
                {g.entries.map((e) => {
                  const isOpen = expandedId === e.id;
                  return (
                    <React.Fragment key={e.id}>
                      <div className={styles.entry}>
                        <span className={styles.entrySummary}>
                          {e.senderName} sent an email:{' '}
                          <span className={styles.entrySubject}>{e.subject}</span>
                        </span>
                        <Badge appearance="outline" color={STATUS_COLOR[e.status]}>
                          {STATUS_LABEL[e.status]}
                        </Badge>
                        <span className={styles.entryTime}>{formatTime(e.sentAt)}</span>
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={isOpen ? <ChevronDown20Regular /> : <ChevronUp20Regular />}
                          onClick={() => setExpandedId(isOpen ? null : e.id)}
                          aria-label={isOpen ? 'Hide details' : 'Show details'}
                          className={styles.expandButton}
                        />
                      </div>
                      {isOpen && (
                        <div className={styles.details}>
                          <div className={styles.detailsLines}>
                            {e.recipients && e.recipients.length > 0 && (
                              <span>
                                <span className={styles.detailsLabel}>Recipients:</span>
                                {e.recipients.join(', ')}
                              </span>
                            )}
                            <span>
                              <span className={styles.detailsLabel}>Subject:</span>
                              {e.subject}
                            </span>
                          </div>
                          {e.onViewEmail && (
                            <Button appearance="secondary" onClick={e.onViewEmail}>
                              View email
                            </Button>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </BaseDialog>
  );
};

export default EmailHistoryDialog;
